import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@lib/supabase/client";
import { PlatformLogo } from "@shared/components/common/PlatformLogo";

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
  whatsapp: string;
  twitter: string;
  instagram: string;
}

const Footer = () => {
  const { data: contactInfo } = useQuery({
    queryKey: ['footer-contact-info'],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', 'contact_info')
        .maybeSingle();
      return (data?.setting_value as unknown as ContactInfo) || null;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const email = contactInfo?.email || 'Dietitech2@gmail.com';
  const phone = contactInfo?.phone || '0533164917';
  const whatsapp = contactInfo?.whatsapp || '966533164917';
  const address = contactInfo?.address || 'المملكة العربية السعودية';

  return (
    <div className="mt-12 pb-6 print:hidden relative z-10 border-t border-border pt-8 bg-muted/40">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* About Section */}
          <div>
            <PlatformLogo variant="transparent" className="h-6 mb-4" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              منصة رقمية متخصصة في تطوير أدوات إلكترونية مبتكرة للممارسين الصحيين
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">روابط سريعة</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">
                  الرئيسية
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="text-muted-foreground hover:text-primary transition-colors">
                  آراء العملاء
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors">
                  الأسئلة الشائعة
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
                  تواصل معنا
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">سياسات الموقع</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">
                  سياسة الخصوصية وشروط الاستخدام
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-muted-foreground hover:text-primary transition-colors">
                  سياسة الاسترجاع
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">تواصل معنا</h3>
            <div className="flex flex-col gap-2 mb-2 text-sm text-muted-foreground">
              <a 
                href={`https://mail.google.com/mail/?view=cm&to=${email}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="w-px h-4 bg-border"></span>
                <span dir="ltr">{email}</span>
              </a>
              <a 
                href={`https://wa.me/${whatsapp}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="w-px h-4 bg-border"></span>
                <span dir="ltr">{phone}</span>
              </a>
            </div>
            <p className="text-sm text-muted-foreground">{address}</p>
          </div>
        </div>

        {/* Verification Badge */}
        <div className="flex justify-center mb-8">
          <a 
            href="#" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img 
              src="/images/saudi-business-center-logo.png" 
              alt="موثق لدى مركز الأعمال" 
              className="h-24 w-auto"
            />
            <span className="text-sm font-medium text-muted-foreground">موثق لدى مركز الأعمال</span>
          </a>
        </div>

        <div className="text-center pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground">
            © 2026 DietiTech - جميع الحقوق محفوظة
          </p>
          <p className="text-xs mt-2 text-muted-foreground">
            تم التطوير بواسطة DietiTech
          </p>
        </div>
      </div>
    </div>
  );
};

export default Footer;